import React, { useRef, useState } from "react";
import Alerts from "../../util/Alerts";
import BlkFormik from "../../coms/BlkFormik";
import { FormikHelpers } from "formik";
import { Button, Container, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import {
    HealthInformationRow,
    initialPolicyValues,
    PolicyColumnLabels,
    policyExcelSchema,
} from "./schemas/policy-excel.schema";
import { getUserPolicy } from "./components/usePolicyGenUser";
import { useCubePolicyUpload } from "./components/useCubePolicyUpload";
import { ReactComponent as IconSinestralidad } from "../../assets/img/icon/dashboard/siniestralidad.svg";
import { ReactComponent as IconDiagnosticos } from "../../assets/img/icon/dashboard/diagnosticos_presentados.svg";
import { getPolicyInformation, policyExcelService } from "./components/policyExcelService";
import { Alert } from "../../interface/alert.interface";
import { useUser } from "../../custom-hooks/useUser";
import { useBoolean } from "../../custom-hooks/useBoolean";
import { PolicyFormValues, RequestResponseVo } from "./schemas/policy.types";
import { PolicyForm } from "./components/PolicyForm";

export const PolicyImportFile: React.FC = () => {
    const alertRef = useRef<Alert>();
    const user = useUser();
    const { isAdmin, isBroker, cdCliente: userClientCode } = user;

    const [isUploading, setIsUploading] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const [formValuesBackup, setFormValuesBackup] = useState<PolicyFormValues | null>(null);
    const [healthInformationRows, setHealthInformationRows] = useState<HealthInformationRow[]>([]);
    const { value: isModalOpen, toggle: toggleModal } = useBoolean(false);

    const { policyOptions, loading, fetchData } = useCubePolicyUpload(user);
    const buildQuery = getUserPolicy(isAdmin);

    const showError = (msg: string) => alertRef.current?.show_error(msg);
    const showInfo = (msg: string) => alertRef.current?.show_info(msg);

    const processResponse = (response?: RequestResponseVo) => {
        if (!response) return;
        if ("success" === response.status || !response.status) {
            setHealthInformationRows(response.cubeData || []);
        }
        if ("warning" === response.status) toggleModal();
        showInfo(response.message);
    };

    const handleProcessUpload = async (values: PolicyFormValues, overwrite = false) => {
        if (!overwrite) setFormValuesBackup(values);
        setIsUploading(true);
        try {
            const response = await policyExcelService(values, alertRef, overwrite);
            processResponse(response);
        } catch {
            showError("Error inesperado al subir archivo");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDataByPolicy = async (
        idClient: number,
        policy: string,
        idQuotations: number[],
        idInsuranceCompany?: number
    ) => {
        if (!idInsuranceCompany) {
            showError("Aseguradora no válida");
            return;
        }
        setLoadingTable(true);
        try {
            const results = await Promise.all(
                idQuotations.map(idQ => getPolicyInformation(idClient, policy, idQ, idInsuranceCompany, alertRef))
            );
            setHealthInformationRows(results.flatMap(r => r.cubeData || []));
        } catch {
            showError("Error al cargar datos de la tabla");
        } finally {
            setLoadingTable(false);
        }
    };

    const handleSubmit = (values: PolicyFormValues, actions: FormikHelpers<PolicyFormValues>) => {
        const idClient = isAdmin || isBroker ? values.cdCliente : userClientCode;
        fetchData(buildQuery(idClient), actions);
    };

    return (
        <>
            <Alerts ref={alertRef} />

            <Container>
                <div className="siniestralidad-container">
                    <div className="header-section">
                        <div className="icon-wrapper">
                            <IconSinestralidad />
                        </div>
                        <h2 className="title-label">Carga de pólizas</h2>
                    </div>
                    <BlkFormik
                        initialValues={initialPolicyValues}
                        validationSchema={policyExcelSchema}
                        onSubmit={handleSubmit}
                    >
                        {() => (
                            <PolicyForm
                                alertRef={alertRef}
                                loading={loading}
                                isUploading={isUploading}
                                policyOptions={policyOptions}
                                setHealthInformation={setHealthInformationRows}
                                handleDataByPolicy={handleDataByPolicy}
                                handleProcessUpload={handleProcessUpload}
                            />
                        )}
                    </BlkFormik>
                </div>
            </Container>

            {healthInformationRows.length > 0 && (
                <Container>
                    <div className="siniestralidad-container">
                        <div className="header-section">
                            <div className="icon-wrapper">
                                <IconDiagnosticos />
                            </div>
                            <h2 className="title-label">Resultados encontrados</h2>
                        </div>
                        <div style={{ maxHeight: "500px", overflow: "auto" }}>
                            <Table hover responsive className="align-middle">
                                <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                                    <tr>
                                        {Object.keys(healthInformationRows[0]).map(key => (
                                            <th key={key}>{PolicyColumnLabels[key] || key}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {healthInformationRows.map(row => (
                                        <tr key={row.policy}>
                                            {Object.entries(row).map(([_, value], i) => (
                                                <td key={`${row.policy}-${i}`}>
                                                    {typeof value === "number"
                                                        ? value.toLocaleString("es-EC", { minimumFractionDigits: 2 })
                                                        : value}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Container>
            )}

            <Modal isOpen={isModalOpen} toggle={toggleModal} className="text-center mx-auto">
                <ModalHeader toggle={toggleModal} className="justify-content-center title" tag="h4">
                    Advertencia de Duplicidad
                </ModalHeader>
                <ModalBody>
                    Existe duplicidad de datos en relación a la fecha de periodo.
                    <br />
                    <b>¿Desea sobreescribir la información existente?</b>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        Cancelar
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => formValuesBackup && handleProcessUpload(formValuesBackup, true)}
                    >
                        Sí, sobreescribir
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={isUploading || loadingTable} centered>
                <ModalBody className="text-center">
                    <i className="ml-2 fa-solid fa-spinner fa-pulse" />
                    <div className="mt-3">Procesando información...</div>
                </ModalBody>
            </Modal>
        </>
    );
};
